import { useEffect, useState } from 'react';
import { create } from 'zustand';

import type { TimeFormat } from '../utils/tradeTime';

/**
 * Which label the Time column shows, shared by every ledger surface (desktop
 * terminal, mobile sheet) so the two never disagree, and remembered per
 * browser — a trader who prefers "x ago" prefers it on the next load too.
 */

const STORAGE_KEY = 'snappy.timeFormat.v1';

function read(): TimeFormat {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === 'relative'
            ? 'relative'
            : 'clock';
    } catch {
        return 'clock';
    }
}

function persist(format: TimeFormat): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, format);
    } catch {
        // storage unavailable — the choice just won't persist
    }
}

interface TimeFormatState {
    format: TimeFormat;
    toggle: () => void;
}

export const useTimeFormatStore = create<TimeFormatState>((set) => ({
    format: read(),
    toggle: () =>
        set((state) => {
            const format =
                state.format === 'clock'
                    ? ('relative' as const)
                    : ('clock' as const);
            persist(format);
            return { format };
        }),
}));

/**
 * The column's mode plus its toggle. Relative labels go stale on their own, so
 * this re-renders the caller every 15s while they're showing — and never when
 * the column is a wall clock.
 */
export function useTimeFormat(): { format: TimeFormat; toggle: () => void } {
    const format = useTimeFormatStore((s) => s.format);
    const toggle = useTimeFormatStore((s) => s.toggle);
    const [, tick] = useState(0);

    useEffect(() => {
        if (format !== 'relative') return;
        const id = setInterval(() => tick((n) => n + 1), 15_000);
        return () => clearInterval(id);
    }, [format]);

    return { format, toggle };
}
